import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { getIdToken } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import { 
  FileSpreadsheet, Download, Loader2, AlertCircle, CheckCircle, Clock, 
  Upload, Trash2, X, Settings, ChevronDown, ChevronUp, Save, RotateCcw 
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { TapscanSettings, TapscanProcessingQueue, Station } from "@shared/schema";

export default function TapscanProcessor() {
  const { toast } = useToast();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedStationId, setSelectedStationId] = useState("");
  const [stationDisplayName, setStationDisplayName] = useState("");
  const [format, setFormat] = useState<'listener' | 'viewer'>('listener');
  const [citationText, setCitationText] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [promotingItems, setPromotingItems] = useState<Set<string>>(new Set());
  const [deletingItem, setDeletingItem] = useState<TapscanProcessingQueue | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Settings state - only store what users can edit
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  
  // Hardcoded values
  const HARDCODED_SETTINGS = {
    maxTokens: 2500,
    batchSize: 5,
    delayBetweenBatches: 1000,
    temperature: 0.3,
    reasoningEffort: "minimal",
    verbosity: "concise",
  };
  
  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast({
          title: "Invalid File",
          description: "Please upload an Excel file (.xlsx or .xls)",
          variant: "destructive",
        });
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
      // Auto-suggest citation based on filename and station
      const year = new Date().getFullYear();
      const month = new Date().toLocaleString('default', { month: 'long' });
      setCitationText(`Nielsen Tapscan Data, ${month} ${year}`);
    }
  };

  // Fetch stations
  const { data: stations = [], isLoading: isLoadingStations } = useQuery<Station[]>({
    queryKey: ['/api/stations'],
  });

  // Fetch queue data
  const { data: queueData = [], isLoading, refetch } = useQuery<TapscanProcessingQueue[]>({
    queryKey: ['/api/tapscan-queue'],
    refetchInterval: 3000, // Poll every 3 seconds
  });

  // Fetch Tapscan settings
  const { data: fetchedSettings, isLoading: isLoadingSettings } = useQuery<TapscanSettings>({
    queryKey: ['/api/tapscan-settings'],
  });

  useEffect(() => {
    if (fetchedSettings) {
      setSystemPrompt((fetchedSettings as any).systemPrompt || '');
    }
  }, [fetchedSettings]);

  // Save settings mutation
  const saveSettings = useMutation({
    mutationFn: async () => {
      const settingsToSave = {
        systemPrompt: systemPrompt,
        ...HARDCODED_SETTINGS,
      };
      return apiRequest('/api/tapscan-settings', 'PUT', settingsToSave);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/tapscan-settings'] });
      toast({
        title: "Settings Saved",
        description: "Tapscan processor settings have been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Save Failed",
        description: error.message || "Failed to save settings. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Add to queue mutation
  const addToQueue = useMutation({
    mutationFn: async () => {
      if (!selectedFile || !selectedStationId) {
        throw new Error("Please select both a file and station");
      }

      const selectedStation = stations.find(s => s.id === selectedStationId);
      if (!selectedStation) {
        throw new Error("Invalid station selected");
      }

      const base64File = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result?.toString().split(',')[1];
          if (base64) {
            resolve(base64);
          } else {
            reject(new Error('Failed to read file'));
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });

      return apiRequest('/api/tapscan-queue', 'POST', {
        fileName: selectedFile.name,
        fileData: base64File,
        stationName: selectedStation.name,
        stationDisplayName: stationDisplayName.trim() || selectedStation.name,
        stationId: selectedStationId,
        format: format,
        citationText: citationText || `Nielsen Tapscan Data, ${new Date().toLocaleDateString()}`,
      });
    },
    onSuccess: () => {
      toast({
        title: "Added to Queue",
        description: "Your file has been added to the processing queue.",
      });
      setSelectedFile(null);
      setSelectedStationId("");
      setStationDisplayName("");
      setCitationText("");
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      refetch();
    },
    onError: (error: Error) => {
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to add file to queue. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Cancel processing mutation
  const cancelProcessing = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest(`/api/tapscan-queue/${id}/cancel`, 'POST');
    },
    onSuccess: () => {
      toast({
        title: "Processing Cancelled",
        description: "The file processing has been cancelled.",
      });
      refetch();
    },
    onError: (error: Error) => {
      toast({
        title: "Cancel Failed",
        description: error.message || "Failed to cancel processing.",
        variant: "destructive",
      });
    },
  });

  // Delete item mutation
  const deleteItem = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest(`/api/tapscan-queue/${id}`, 'DELETE');
    },
    onSuccess: () => {
      toast({
        title: "Item Deleted",
        description: "The queue item has been removed.",
      });
      refetch();
    },
    onError: (error: Error) => {
      toast({
        title: "Delete Failed",
        description: error.message || "Failed to delete item.",
        variant: "destructive",
      });
    },
  });

  // Retry item mutation (for error/cancelled items)
  const retryItem = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest(`/api/tapscan-queue/${id}/retry`, 'POST');
    },
    onSuccess: () => {
      toast({
        title: "Retry Queued",
        description: "The item has been queued for reprocessing.",
      });
      refetch();
    },
    onError: (error: Error) => {
      toast({
        title: "Retry Failed",
        description: error.message || "Failed to retry item.",
        variant: "destructive",
      });
    },
  });

  // Download completed file via API endpoint (optimized - doesn't require outputData in list response)
  const downloadFile = async (item: TapscanProcessingQueue) => {
    if (item.status === 'completed' || (item.status === 'error' && item.factsFound > 0)) {
      try {
        const token = await getIdToken();
        const headers: Record<string, string> = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`/api/tapscan-queue/${item.id}/download`, {
          headers,
          credentials: 'include',
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to download file');
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = item.outputFileName || `${item.stationName}_facts.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        toast({
          title: "Download Failed",
          description: error instanceof Error ? error.message : "Failed to download file.",
          variant: "destructive",
        });
      }
    }
  };
  
  // Handle promoting queue item to knowledge base
  const handlePromote = async (item: TapscanProcessingQueue) => {
    // Add this item to the promoting set
    setPromotingItems(prev => new Set(prev).add(item.id));
    
    try {
      // Use the stationId from the queue item if available
      const stationId = item.stationId;
      
      if (!stationId) {
        toast({
          title: "Station Not Found",
          description: "This file was processed without a station association. Please re-process with a station selected.",
          variant: "destructive",
        });
        // Remove from promoting set
        setPromotingItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(item.id);
          return newSet;
        });
        return;
      }
      
      // Call the promotion endpoint
      const promoteResponse = await apiRequest(`/api/tapscan-queue/${item.id}/promote`, 'POST', { stationId });
      
      toast({
        title: "Facts Promoted",
        description: "Facts have been successfully promoted to the knowledge base.",
      });
      
      // Refresh the queue data
      refetch();
    } catch (error) {
      console.error('Error promoting item:', error);
      toast({
        title: "Promotion Failed",
        description: error instanceof Error ? error.message : "Failed to promote facts to knowledge base.",
        variant: "destructive",
      });
    } finally {
      // Always remove from promoting set when done
      setPromotingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
    }
  };


  // Reset to defaults
  const handleResetDefaults = () => {
    const defaultSystemPrompt = `ROLE
Return EXACTLY ONE clean, grammatical sentence about a station's audience using the four inputs below.
This function is called repeatedly. On each call:
• If {pct} is provided (non‑empty) → produce a percentage fact.
• Else if {index} is provided (non‑empty) → produce an index fact.
• Else (both empty) → return an empty string.

INPUTS (per call)
{station}  — Station name.
{raw_fact} — Raw fact text to be rewritten into a natural action or trait (do not output the words "raw fact").
{pct}      — Percentage value (present ONLY for percentage facts).
{index}    — Index value (present ONLY for index facts; 100 = average adult).

ABSOLUTE OUTPUT CONTRACT
• Output ONE sentence only. Start with a capital letter (or number) and end with ONE period.
• No labels, bullets, JSON, questions, placeholders, or extra commentary.
• Never output variable names or instructional phrases (e.g., "raw fact text to convert", "rewritten action"). Use the values only.
• Preserve meaningful parentheses from {raw_fact}, but remove meaningless tokens like "(any)" and "(HHLD)".

SUBJECT
• Use "{station} listeners" as the subject.
• If {raw_fact} contains "(HHLD)" or the word "household", use "listener households" as the subject and DO NOT include "(HHLD)" in the sentence.
• Plural subject → plural verb ("listeners are / have / plan"; "listener households are / have / plan"). Never use "is" with these subjects.

TIMEFRAME (must be kept and read smoothly)
If {raw_fact} contains a timeframe phrase such as "past 7 days", "past 30 days", "past 3 months", "past 6 months", "past 12 months", or "next 12 months",
you MUST include that phrase exactly once and make it flow with a preposition:
• Use "in the past X …" for past timeframes (e.g., "in the past 12 months").
• Use "in the next 12 months …" for future plans.

REWRITE {raw_fact} INTO A NATURAL ACTION (minimal, generic)
1) Split {raw_fact} at the LAST colon ":". Treat the text after the last colon as the main phrase; trim spaces. Keep qualifiers and proper nouns.
2) Replace "/" with " or " in generic phrases (e.g., "church/temple" → "church or temple"). Do not alter brand/place names.
3) If the phrase ends with "involvement", rewrite as "participate in …" (never "do … involvement").
4) If the phrase is "No" or "None", use a negative action: "did not …" or "have not …" (include the timeframe if present).
5) For generic singular activities/venues, add an article and lowercase the noun ("an art museum", "a wine tasting"). Keep proper names as written.
6) Count buckets (e.g., "1 time", "2 times", "4 times or more", "1 – 2", "5 – 9"): write "have [verb] [count] [object] …".
   • If the object mentions "trip(s)", use the verb "made" (e.g., "have made 4 trips …").
7) If the phrase indicates a ZIP/Postal reference (e.g., contains "ZIP", "Zip", or any 5‑digit codes like 77005, 77401):
   • Use a location qualifier such as "in ZIP code 77005" or "in ZIP codes 77005, 77006, and 77007".
   • Do NOT add "a/an" before ZIP codes. Keep "ZIP" uppercase.
8) Keep verbs simple and literal. Prefer: "regularly …", "have …", "have attended/visited …", "plan to …", "use / used or visited …".
   If unclear, use "engage in …" exactly.
9) Geographic wording: insert needed articles and prepositions for idiomatic English
   • "outside the continental U.S.", "in the U.S.", "across the U.S."

PERCENTAGE FACT (when {pct} is provided)
• Write as: "<formatted percent value>% of <subject> <natural action>."
• Formatting for {pct}:
  – If 0 < {pct} ≤ 1 → output {pct}×100 with one decimal; strip trailing ".0".
  – If 1 < {pct} ≤ 100 → output with at most one decimal; strip trailing ".0".
• Do NOT include any comparison language.
• Include the timeframe (if present in {raw_fact}) exactly once, with "in the …".

INDEX FACT (when {index} is provided)
• Compute lift = |{index} − 100|. No decimals unless {index} includes them; strip trailing ".0".
• If {index} > 100 → "{station} listeners are <lift>% more likely to <natural action> than the average adult."
• If {index} < 100 → "{station} listeners are <lift}% less likely to <natural action> than the average adult."
• If {index} = 100 → "{station} listeners are about as likely to <natural action> as the average adult."
• If the action is negative ("did not …" / "have not …"):
  – When {index} < 100 → write "less likely to <base verb> …" (drop "not").
  – When {index} > 100 → write "more likely to not <base verb> …" (use "not"; never "to did not …").
• Include the timeframe (if present in {raw_fact}) exactly once, with "in the …".
• Always use the exact phrase "the average adult"; never say "U.S. average".

ONE‑SENTENCE ENFORCEMENT
• Return ONE sentence only, with ONE period at the end.
• Do not emit extra punctuation that creates another sentence.
• Do not output variable names or instructional text; never write the words "raw fact".`;
    setSystemPrompt(defaultSystemPrompt);
    toast({
      title: "Settings Reset",
      description: "Processing settings have been reset to defaults.",
    });
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}m ${secs}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${mins}m`;
    }
  };
  
  const getElapsedTime = (item: TapscanProcessingQueue) => {
    if (!item.createdAt) return '-';
    
    const startTime = new Date(item.createdAt).getTime();
    let endTime: number;
    
    if (item.status === 'completed' || item.status === 'error' || item.status === 'cancelled') {
      // Use completedAt if available, otherwise use current time
      endTime = item.completedAt ? new Date(item.completedAt).getTime() : Date.now();
    } else {
      // Still processing, use current time
      endTime = Date.now();
    }
    
    const elapsedSeconds = Math.floor((endTime - startTime) / 1000);
    return formatTime(elapsedSeconds);
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getStatusDisplay = (item: TapscanProcessingQueue) => {
    // If processing and has currentStep, use that for more descriptive status
    if (item.status === 'processing' && item.currentStep) {
      // Parse the currentStep to show user-friendly text
      if (item.currentStep.includes('Parsing')) {
        return { text: 'Parsing Excel', variant: 'default' as const };
      }
      if (item.currentStep.includes('Generating prompts')) {
        return { text: 'Preparing batch', variant: 'default' as const };
      }
      if (item.currentStep.includes('Batch job submitted') || item.currentStep.includes('Uploading')) {
        return { text: 'Uploading batch', variant: 'default' as const };
      }
      if (item.currentStep.includes('Processing batch results')) {
        return { text: 'Processing results', variant: 'default' as const };
      }
      if (item.currentStep.includes('Creating') || item.currentStep.includes('Formatting')) {
        return { text: 'Creating Excel', variant: 'default' as const };
      }
      
      // If has batch status, convert to user-friendly text
      if (item.batchStatus) {
        switch (item.batchStatus) {
          case 'validating':
            return { text: 'Validating batch', variant: 'secondary' as const };
          case 'in_progress':
            return { text: 'Analyzing rows', variant: 'default' as const };
          case 'finalizing':
            return { text: 'Finalizing', variant: 'default' as const };
          case 'completed':
            return { text: 'Downloading results', variant: 'default' as const };
          case 'failed':
            return { text: 'Failed', variant: 'destructive' as const };
          case 'expired':
            return { text: 'Expired', variant: 'destructive' as const };
          case 'cancelling':
            return { text: 'Cancelling', variant: 'secondary' as const };
          case 'cancelled':
            return { text: 'Cancelled', variant: 'secondary' as const };
          default:
            return { text: 'Processing', variant: 'default' as const };
        }
      }
    }
    
    // Default status display
    switch (item.status) {
      case 'pending':
        return { text: 'Queued', variant: 'secondary' as const };
      case 'processing':
        return { text: 'Processing', variant: 'default' as const };
      case 'completed':
        return { text: 'Completed', variant: 'outline' as const };
      case 'error':
        return { text: 'Error', variant: 'destructive' as const };
      case 'cancelled':
        return { text: 'Cancelled', variant: 'secondary' as const };
      default:
        return { text: item.status, variant: 'outline' as const };
    }
  };

  if (isLoadingSettings || isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-purple-600" />
              <CardTitle>Nielsen Tapscan Data Processor</CardTitle>
            </div>
            <CardDescription>
              Transform raw Tapscan exports into clean, RAG-ready fact sheets
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Upload Section */}
        <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            <h3 className="font-semibold">Upload New File</h3>
          </div>
          <p className="text-sm text-red-600 font-medium">
            Before processing any dataset, ensure the OpenAI Batch API dashboard is open in another browser window so you can monitor progress and prevent duplicate batches. Duplicate processing can result in hundreds of dollars in unnecessary API charges. As a best practice, avoid refreshing the browser while a batch is running.
          </p>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <Label htmlFor="station-select">Station (for association/promotion)</Label>
                <Select 
                  value={selectedStationId} 
                  onValueChange={setSelectedStationId}
                  disabled={addToQueue.isPending || stations.length === 0}
                >
                  <SelectTrigger id="station-select" data-testid="select-station">
                    <SelectValue placeholder={stations.length === 0 ? "No stations available" : "Select a station"} />
                  </SelectTrigger>
                  <SelectContent>
                    {stations.map((station) => (
                      <SelectItem key={station.id} value={station.id}>
                        {station.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label htmlFor="station-display-name">Station (to be used in fact rewrites)</Label>
                <Input
                  id="station-display-name"
                  data-testid="input-station-display-name"
                  type="text"
                  value={stationDisplayName}
                  onChange={(e) => setStationDisplayName(e.target.value)}
                  placeholder="e.g., KUNC or Colorado Public Radio"
                  disabled={addToQueue.isPending}
                />
              </div>
              <div className="w-40">
                <Label htmlFor="format-select">Format</Label>
                <Select 
                  value={format} 
                  onValueChange={(value) => setFormat(value as 'listener' | 'viewer')}
                  disabled={addToQueue.isPending}
                >
                  <SelectTrigger id="format-select" data-testid="select-format">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="listener">Radio</SelectItem>
                    <SelectItem value="viewer">Television</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <Label htmlFor="file-upload">Tapscan Excel File</Label>
                <Input
                  id="file-upload"
                  data-testid="input-file-upload"
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".xlsx,.xls"
                  disabled={addToQueue.isPending}
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="citation-input">Citation Text</Label>
                <Input
                  id="citation-input"
                  data-testid="input-citation"
                  type="text"
                  value={citationText}
                  onChange={(e) => setCitationText(e.target.value)}
                  placeholder="e.g., Nielsen Tapscan Data, November 2024"
                  disabled={addToQueue.isPending}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => addToQueue.mutate()}
                disabled={!selectedFile || !selectedStationId || addToQueue.isPending}
                data-testid="button-add-to-queue"
              >
                {addToQueue.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Add to Queue
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Processing Queue Table */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Processing Queue</h3>
            <div className="text-sm text-gray-500">
              {queueData.length} total items
            </div>
          </div>

          {/* Full-width progress bar - only show when actively processing */}
          {queueData.some(item => item.status === 'processing' && item.progress && item.progress > 0 && item.progress < 100) && (
            <div className="w-full">
              {queueData
                .filter(item => item.status === 'processing' && item.progress && item.progress > 0 && item.progress < 100)
                .map((item) => (
                  <div key={item.id} className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Processing: {item.fileName}</span>
                      <span className="text-gray-500">{Math.round(item.progress || 0)}%</span>
                    </div>
                    <Progress 
                      value={item.progress || 0} 
                      className="w-full h-2" 
                    />
                    {item.currentStep && (
                      <p className="text-xs text-gray-500">{item.currentStep}</p>
                    )}
                  </div>
                ))}
            </div>
          )}

          {queueData.length === 0 ? (
            <div className="border rounded-lg p-8 text-center text-gray-500">
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No files in the queue</p>
              <p className="text-sm mt-1">Upload a Tapscan file to get started</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File Name</TableHead>
                    <TableHead>Station</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Facts Found</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {queueData.map((item) => {
                    const statusInfo = getStatusDisplay(item);
                    const statusIcon = statusInfo.text === 'Processing' ? 
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" /> :
                      statusInfo.text === 'Completed' ? 
                      <CheckCircle className="h-3 w-3 mr-1" /> :
                      statusInfo.text === 'Error' || statusInfo.text === 'Failed' ? 
                      <AlertCircle className="h-3 w-3 mr-1" /> :
                      statusInfo.text === 'Queued' || statusInfo.text === 'Pending' ? 
                      <Clock className="h-3 w-3 mr-1" /> :
                      statusInfo.text === 'Cancelled' || statusInfo.text === 'Cancelling' ? 
                      <X className="h-3 w-3 mr-1" /> : null;
                    
                    return (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium max-w-[200px] truncate">
                          {item.fileName}
                        </TableCell>
                        <TableCell>{item.stationName}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge 
                              variant={statusInfo.variant}
                              className={statusInfo.text === 'Completed' ? 'text-green-600' : ''}
                            >
                              {statusIcon}
                              {statusInfo.text}
                            </Badge>
                            {item.status === 'error' && item.errorMessage && (
                              <span className="text-xs text-red-500 max-w-[200px] truncate" title={item.errorMessage}>
                                {item.errorMessage}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {item.factsFound > 0 ? item.factsFound.toLocaleString() : '-'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {getElapsedTime(item)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {item.status === 'processing' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => cancelProcessing.mutate(item.id)}
                                data-testid={`button-cancel-${item.id}`}
                              >
                                <X className="h-3 w-3" />
                                Cancel
                              </Button>
                            )}
                            {(item.status === 'completed' || (item.status === 'error' && item.factsFound > 0)) && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => downloadFile(item)}
                                  data-testid={`button-download-${item.id}`}
                                >
                                  <Download className="h-3 w-3" />
                                  Download
                                </Button>
                                {item.status === 'completed' && !item.promoted && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handlePromote(item)}
                                    data-testid={`button-promote-${item.id}`}
                                    className="text-green-600 hover:text-green-700"
                                    disabled={promotingItems.has(item.id)}
                                  >
                                    {promotingItems.has(item.id) ? (
                                      <>
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                        Promoting...
                                      </>
                                    ) : (
                                      <>
                                        <Upload className="h-3 w-3" />
                                        Promote
                                      </>
                                    )}
                                  </Button>
                                )}
                                {item.promoted && (
                                  <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                                    Promoted
                                  </Badge>
                                )}
                              </>
                            )}
                            {(item.status === 'error' || item.status === 'cancelled') && !item.promoted && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => retryItem.mutate(item.id)}
                                data-testid={`button-retry-${item.id}`}
                                className="text-orange-600 hover:text-orange-700"
                                disabled={retryItem.isPending}
                              >
                                {retryItem.isPending ? (
                                  <>
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    Retrying...
                                  </>
                                ) : (
                                  <>
                                    <RotateCcw className="h-3 w-3" />
                                    Retry
                                  </>
                                )}
                              </Button>
                            )}
                            {(item.status === 'completed' || item.status === 'error' || item.status === 'cancelled') && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setDeletingItem(item)}
                                data-testid={`button-delete-${item.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Settings Section (Collapsible) */}
        <Collapsible open={showSettings} onOpenChange={setShowSettings}>
          <CollapsibleTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Processor Settings
              </div>
              {showSettings ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-4 space-y-4 border rounded-lg p-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Configure the AI prompt that transforms Tapscan data into natural language facts.
                Variables: {'{station}'}, {'{raw_fact}'}, {'{pct}'}, {'{index}'}, {'{format}'} (listener/viewer)
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="system-prompt">Transformation Prompt</Label>
              <Textarea
                id="system-prompt"
                data-testid="input-system-prompt"
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="Enter the system prompt for transforming Tapscan data..."
                className="min-h-[200px] font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                This prompt is sent with each data row. Use variables to insert data values.
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => saveSettings.mutate()}
                disabled={saveSettings.isPending}
                data-testid="button-save-settings"
              >
                {saveSettings.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Settings
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={handleResetDefaults}
                data-testid="button-reset-settings"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset to Default
              </Button>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingItem} onOpenChange={() => setDeletingItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deletingItem?.promoted ? "Delete Promoted Item?" : "Delete Queue Item?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deletingItem?.promoted ? (
                <>
                  <strong className="text-destructive">Warning:</strong> This item has been promoted to the knowledge base. 
                  Deleting it will remove <strong>{deletingItem.factsFound || 0} facts</strong> from the knowledge base 
                  and affect any VBRs that use this data.
                  <br /><br />
                  This action cannot be undone.
                </>
              ) : (
                "This will permanently delete the queue item and its processing data. This action cannot be undone."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deletingItem) {
                  deleteItem.mutate(deletingItem.id);
                  setDeletingItem(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingItem?.promoted ? "Delete Anyway" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}